import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { LayoutDashboard, PlusCircle, Settings, LogOut, Building2, ChevronRight, FileText, User, Users, Stamp, Clock, HelpCircle, Shield, ExternalLink, MessageSquare, Megaphone, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';

const navItems = [
  { label: 'Dashboard', path: '/admin', icon: LayoutDashboard },
  { label: 'Neue Analyse', path: '/admin/analyse/neu', icon: PlusCircle },
  { label: 'PDF-Firmenname', path: '/admin/pdf-firma', icon: Stamp },
  { label: 'Einstellungen', path: '/admin/einstellungen', icon: Settings, adminOnly: true },
  { label: 'PDF-Einstellungen', path: '/admin/pdf-einstellungen', icon: FileText, adminOnly: true },
  { label: 'Nutzerverwaltung', path: '/admin/nutzer', icon: Users, adminOnly: true },
  { label: 'Umfragen', path: '/admin/umfragen', icon: MessageSquare, adminOnly: true },
  { label: 'Hinweise', path: '/admin/hinweise', icon: Megaphone, adminOnly: true },
  { label: 'Erläuterungsseite', path: '/admin/erlaeuterung', icon: BookOpen, adminOnly: true },
];

export default function AdminLayout() {
  const { user, logout, sessionWarning, remainingSeconds, extendSession } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => { logout(); navigate('/login'); };
  const isAdmin = user?.role === 'admin';
  const visibleNav = navItems.filter(item => !item.adminOnly || isAdmin);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="hidden md:flex md:w-64 flex-col border-r bg-background sticky top-0 h-screen" data-testid="admin-sidebar">
        <div className="p-6 flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
            <Building2 className="h-5 w-5 text-primary-foreground" />
          </div>
          <div>
            <div className="font-semibold text-sm">VerwalterHonorar.de</div>
            <div className="text-xs text-muted-foreground">{user?.name || user?.username || 'Admin'}</div>
          </div>
        </div>
        <Separator />
        <nav className="flex-1 p-4 space-y-1 overflow-auto">
          {visibleNav.map(item => {
            const Icon = item.icon;
            const active = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path} data-testid={`nav-${item.path.split('/').pop()}`}>
                <div className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-150 ${active ? 'bg-secondary font-medium text-foreground' : 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground'}`}>
                  <Icon className="h-4 w-4" />
                  {item.label}
                  {active && <ChevronRight className="h-3 w-3 ml-auto" />}
                </div>
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t space-y-1">
          <Link to="/faq" data-testid="nav-faq">
            <Button variant="ghost" className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground text-sm">
              <HelpCircle className="h-4 w-4" /> FAQ
            </Button>
          </Link>
          <Link to="/info" data-testid="nav-info">
            <Button variant="ghost" className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground text-sm">
              <Shield className="h-4 w-4" /> Datenschutz
            </Button>
          </Link>
          <Separator className="my-1" />
          <Link to="/admin/profil" data-testid="nav-profil">
            <Button variant="ghost" className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground">
              <User className="h-4 w-4" /> Mein Profil
            </Button>
          </Link>
          <Button variant="ghost" className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="h-4 w-4" /> Abmelden
          </Button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto">
        {/* Desktop header bar with session timer */}
        <div className="hidden md:flex items-center justify-end border-b px-6 py-2 sticky top-0 bg-background/95 backdrop-blur-sm z-10" data-testid="desktop-header">
          <div className={`flex items-center gap-2 text-xs font-mono tabular-nums px-3 py-1.5 rounded-full ${sessionWarning ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-secondary/50 text-muted-foreground'}`} data-testid="session-timer">
            <Clock className="h-3.5 w-3.5" />
            <span>Sitzung: {formatTime(remainingSeconds)}</span>
          </div>
        </div>
        {/* Mobile nav */}
        <div className="md:hidden flex items-center justify-between border-b p-4 sticky top-0 bg-background z-10">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            <span className="font-semibold text-sm">VerwalterHonorar.de</span>
          </div>
          <div className="flex items-center gap-1">
            <div className={`flex items-center gap-1 text-[10px] font-mono tabular-nums px-2 py-1 rounded-full mr-1 ${sessionWarning ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-secondary/50 text-muted-foreground'}`} data-testid="session-timer-mobile">
              <Clock className="h-3 w-3" />
              {formatTime(remainingSeconds)}
            </div>
            {visibleNav.map(item => {
              const Icon = item.icon;
              const active = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path}>
                  <Button variant={active ? "secondary" : "ghost"} size="sm" className="h-8 w-8 p-0" data-testid={`mobile-nav-${item.path.split('/').pop()}`}>
                    <Icon className="h-4 w-4" />
                  </Button>
                </Link>
              );
            })}
            <Link to="/admin/profil">
              <Button variant={location.pathname === '/admin/profil' ? "secondary" : "ghost"} size="sm" className="h-8 w-8 p-0">
                <User className="h-4 w-4" />
              </Button>
            </Link>
            <Link to="/faq">
              <Button variant={location.pathname === '/faq' ? "secondary" : "ghost"} size="sm" className="h-8 w-8 p-0" data-testid="mobile-nav-faq">
                <HelpCircle className="h-4 w-4" />
              </Button>
            </Link>
            <Link to="/info">
              <Button variant={location.pathname === '/info' ? "secondary" : "ghost"} size="sm" className="h-8 w-8 p-0" data-testid="mobile-nav-info">
                <Shield className="h-4 w-4" />
              </Button>
            </Link>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={handleLogout}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="p-6 md:p-8 lg:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
